import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Check, Crown, Zap, ArrowRight, Star, Shield, Loader2, AlertTriangle, UserCheck, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const SubscriptionPlans = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [profileComplete, setProfileComplete] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subRes, profileRes] = await Promise.all([
        axios.get(`${API_URL}/saas/plans`),
        axios.get(`${API_URL}/saas/subscription`, { withCredentials: true }).catch(() => null),
        axios.get(`${API_URL}/seller/profile`, { withCredentials: true }).catch(() => null)
      ]);
      
      setPlans(plansRes.data.plans);
      if (subRes?.data) {
        setSubscription(subRes.data);
      }
      if (profileRes?.data) {
        setProfileComplete(profileRes.data.profile_complete || false);
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planKey) => {
    if (!user) {
      toast.error('Faça login para assinar um plano');
      navigate('/login');
      return;
    }

    if (user.role !== 'seller') {
      toast.error('Apenas vendedores podem assinar planos');
      return;
    }

    // Perfil incompleto é apenas informativo (não bloqueia mais o pagamento).
    // Usuário completa após ativar a assinatura.

    // Novo fluxo: Checkout Transparente. Redirecionamos para a página unificada.
    navigate(`/checkout?target=subscription&target_id=${planKey}`);
  };

  const getPlanIcon = (planKey) => {
    switch (planKey) {
      case 'basic': return <Sparkles className="w-6 h-6" />;
      case 'premium': return <Zap className="w-6 h-6" />;
      case 'pro': return <Star className="w-6 h-6" />;
      case 'advanced': return <Shield className="w-6 h-6" />;
      case 'scale':
      case 'business':
      case 'elite':
      case 'exclusive':
        return <Crown className="w-6 h-6" />;
      default: return <Zap className="w-6 h-6" />;
    }
  };

  const getPlanColor = (planKey) => {
    switch (planKey) {
      case 'basic': return 'from-slate-500 to-slate-600';
      case 'premium': return 'from-blue-500 to-blue-600';
      case 'pro': return 'from-purple-500 to-purple-600';
      case 'advanced': return 'from-indigo-500 to-indigo-600';
      case 'scale': return 'from-cyan-500 to-cyan-600';
      case 'business': return 'from-emerald-500 to-emerald-600';
      case 'elite': return 'from-amber-500 to-amber-600';
      case 'exclusive': return 'from-rose-500 to-rose-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="plans-title">
            Escolha seu Plano
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Comece a vender mais com automação inteligente via WhatsApp IA. 
            Todos os planos incluem acesso ao painel completo.
          </p>
          
          {subscription && (
            <div className="mt-6 inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 rounded-full px-4 py-2">
              <Check className="w-5 h-5 text-indigo-400" />
              <span className="text-indigo-300">
                Plano atual: <strong>{plans[subscription.plan]?.name}</strong>
              </span>
            </div>
          )}

          {/* Banner de Perfil Incompleto */}
          {profileComplete === false && (
            <div className="mt-6 max-w-xl mx-auto bg-amber-500/15 border border-amber-500/30 rounded-xl p-4 flex items-center gap-4" data-testid="profile-incomplete-banner">
              <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0" />
              <div className="text-left">
                <p className="text-amber-200 text-sm font-medium">Cadastro incompleto</p>
                <p className="text-amber-300/70 text-xs mt-0.5">Complete seus dados para poder assinar um plano</p>
              </div>
              <Button
                onClick={() => navigate('/seller/complete-profile')}
                size="sm"
                className="ml-auto bg-amber-500 hover:bg-amber-600 text-black font-medium whitespace-nowrap"
                data-testid="complete-profile-cta"
              >
                <UserCheck className="w-4 h-4 mr-1" />
                Completar
              </Button>
            </div>
          )}
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Object.entries(plans).map(([key, plan]) => {
            const isCurrent = subscription?.plan === key;
            const isExclusive = key === 'exclusive';
            const isPopular = plan.popular;
            
            return (
              <div
                key={key}
                className={`relative bg-slate-800/50 backdrop-blur-sm rounded-2xl border overflow-hidden transition-all duration-300 hover:scale-[1.02] ${
                  isCurrent 
                    ? 'border-indigo-500 ring-2 ring-indigo-500/50' 
                    : isPopular 
                      ? 'border-purple-500/50' 
                      : 'border-slate-700'
                }`}
                data-testid={`plan-card-${key}`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    MAIS POPULAR
                  </div>
                )}

                {/* Current Badge */}
                {isCurrent && (
                  <div className="absolute top-0 left-0 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-br-lg">
                    SEU PLANO
                  </div>
                )}

                {/* Plan Header */}
                <div className={`bg-gradient-to-r ${getPlanColor(key)} p-6`}>
                  <div className="flex items-center gap-3 text-white">
                    {getPlanIcon(key)}
                    <h3 className="text-2xl font-bold">{plan.name}</h3>
                  </div>
                </div>

                {/* Plan Content */}
                <div className="p-6">
                  {/* Price */}
                  <div className="mb-6">
                    {isExclusive ? (
                      <p className="text-2xl font-bold text-white">Sob consulta</p>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-white">
                          R$ {plan.price.toFixed(2)}
                        </span>
                        <span className="text-slate-400">/mês</span>
                      </>
                    )}
                  </div>

                  {/* Credits */}
                  <div className="flex items-center gap-2 mb-6 p-3 bg-slate-700/50 rounded-lg">
                    <Zap className="w-5 h-5 text-amber-400" />
                    <span className="text-white font-medium">
                      {isExclusive ? 'Ilimitado' : plan.credits === 0 ? 'Créditos avulsos' : `${plan.credits} créditos`}
                    </span>
                    <span className="text-slate-400 text-sm">{plan.credits === 0 && !isExclusive ? '(comprar separadamente)' : '/mês'}</span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    onClick={() => handleSubscribe(key)}
                    disabled={isCurrent || isExclusive || processingPlan === key}
                    className={`w-full py-3 font-medium transition-all ${
                      isCurrent
                        ? 'bg-slate-600 cursor-not-allowed'
                        : isExclusive
                          ? 'bg-slate-600 cursor-not-allowed'
                          : `bg-gradient-to-r ${getPlanColor(key)} hover:opacity-90`
                    }`}
                    data-testid={`subscribe-btn-${key}`}
                  >
                    {processingPlan === key ? (
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    ) : isCurrent ? (
                      'Plano Atual'
                    ) : isExclusive ? (
                      'Contate-nos'
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Assinar Agora
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Payment Methods */}
        <div className="mt-12 text-center">
          <p className="text-slate-400 mb-4">Pagamento seguro via</p>
          <div className="flex items-center justify-center gap-6">
            <div className="bg-white/10 px-4 py-2 rounded-lg">
              <img 
                src="https://imgmp.mlstatic.com/org-img/MLB/MP/LOGO/logo-mp-h.png" 
                alt="Mercado Pago"
                className="h-8"
              />
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Shield className="w-4 h-4" />
              <span>PIX, Cartão de Crédito, Boleto</span>
            </div>
          </div>
        </div>

        {/* FAQ/Info */}
        <div className="mt-12 bg-slate-800/30 rounded-2xl p-8 border border-slate-700">
          <h3 className="text-xl font-bold text-white mb-4">Perguntas Frequentes</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-white font-medium mb-2">O que são créditos?</h4>
              <p className="text-slate-400 text-sm">
                Créditos são usados para funcionalidades premium como conversas WhatsApp IA, 
                geração de conteúdo e páginas de venda.
              </p>
            </div>
            <div>
              <h4 className="text-white font-medium mb-2">Posso cancelar a qualquer momento?</h4>
              <p className="text-slate-400 text-sm">
                Sim! Você pode cancelar sua assinatura a qualquer momento. 
                Seu acesso continua até o fim do período pago.
              </p>
            </div>
            <div>
              <h4 className="text-white font-medium mb-2">Como funciona a renovação?</h4>
              <p className="text-slate-400 text-sm">
                Sua assinatura renova automaticamente a cada 30 dias. 
                Você receberá um lembrete antes da cobrança.
              </p>
            </div>
            <div>
              <h4 className="text-white font-medium mb-2">Posso fazer upgrade?</h4>
              <p className="text-slate-400 text-sm">
                Sim! Você pode fazer upgrade a qualquer momento e os créditos 
                restantes serão somados ao novo plano.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
